import { deviceFingerprint, deviceRegistrationBytes } from './devices';
import { concatBytes, fromBase64Url, toBase64Url } from './encoding';
import { signBytes } from './signatures';

const ECDH = { name: 'ECDH', namedCurve: 'P-256' } as const;
const ECDSA = { name: 'ECDSA', namedCurve: 'P-256' } as const;

/** A P-256 private key as JWK, with only the fields needed to import it again. */
export interface PrivateJwk {
  kty: 'EC';
  crv: 'P-256';
  x: string;
  y: string;
  d: string;
}

/**
 * A device's key pairs in storable form. This is secret: it lives only inside
 * the encrypted vault and the encrypted backup file.
 */
export interface DeviceKeyMaterial {
  v: 1;
  id: string;
  encryption: { publicKey: string; privateKey: PrivateJwk };
  signing: { publicKey: string; privateKey: PrivateJwk };
}

/** A device's keys, ready to use. The private keys cannot be exported from these objects. */
export interface DeviceKeys {
  id: string;
  encryptionPublic: Uint8Array<ArrayBuffer>;
  signingPublic: Uint8Array<ArrayBuffer>;
  encryptionPrivate: CryptoKey;
  signingPrivate: CryptoKey;
}

/** Another device's public keys, from the key directory or a QR code. */
export interface PeerDevice {
  id: string;
  encryptionKey: Uint8Array;
  signingKey: Uint8Array;
}

/** Reads a key directory entry, whose keys are base64url. */
export function peerFromDirectory(entry: { id: string; encryptionKey: string; signingKey: string }): PeerDevice {
  return {
    id: entry.id,
    encryptionKey: fromBase64Url(entry.encryptionKey),
    signingKey: fromBase64Url(entry.signingKey),
  };
}

/** True when the peer's keys hash to its claimed device ID. */
export async function peerMatchesId(peer: PeerDevice): Promise<boolean> {
  try {
    return (await deviceFingerprint(peer.encryptionKey, peer.signingKey)) === peer.id;
  } catch {
    return false;
  }
}

function privateJwk(jwk: JsonWebKey): PrivateJwk {
  if (jwk.kty !== 'EC' || jwk.crv !== 'P-256' || !jwk.x || !jwk.y || !jwk.d) {
    throw new Error('Not a P-256 private key');
  }
  return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y, d: jwk.d };
}

function publicFromJwk(jwk: PrivateJwk): Uint8Array<ArrayBuffer> {
  return concatBytes(new Uint8Array([0x04]), fromBase64Url(jwk.x), fromBase64Url(jwk.y));
}

/**
 * Turns stored key material into usable keys. Checks that each stored public
 * key matches its private key's public part and that the ID is their
 * fingerprint, so damaged material is caught before use.
 */
export async function loadDeviceKeys(material: DeviceKeyMaterial): Promise<DeviceKeys> {
  if (material.v !== 1) throw new Error('Unknown device key format');
  const encryptionPublic = fromBase64Url(material.encryption.publicKey);
  const signingPublic = fromBase64Url(material.signing.publicKey);
  if (toBase64Url(publicFromJwk(material.encryption.privateKey)) !== material.encryption.publicKey) {
    throw new Error('The encryption key pair does not match');
  }
  if (toBase64Url(publicFromJwk(material.signing.privateKey)) !== material.signing.publicKey) {
    throw new Error('The signing key pair does not match');
  }
  if ((await deviceFingerprint(encryptionPublic, signingPublic)) !== material.id) {
    throw new Error('The device ID does not match its keys');
  }
  const subtle = globalThis.crypto.subtle;
  const [encryptionPrivate, signingPrivate] = await Promise.all([
    subtle.importKey('jwk', material.encryption.privateKey, ECDH, false, ['deriveBits']),
    subtle.importKey('jwk', material.signing.privateKey, ECDSA, false, ['sign']),
  ]);
  return { id: material.id, encryptionPublic, signingPublic, encryptionPrivate, signingPrivate };
}

/** Creates a new device's ECDH and ECDSA P-256 key pairs. */
export async function generateDeviceKeys(): Promise<{ keys: DeviceKeys; material: DeviceKeyMaterial }> {
  const subtle = globalThis.crypto.subtle;
  const encryption = await subtle.generateKey(ECDH, true, ['deriveBits']);
  const signing = await subtle.generateKey(ECDSA, true, ['sign', 'verify']);
  const encryptionPublic = new Uint8Array(await subtle.exportKey('raw', encryption.publicKey));
  const signingPublic = new Uint8Array(await subtle.exportKey('raw', signing.publicKey));
  const material: DeviceKeyMaterial = {
    v: 1,
    id: await deviceFingerprint(encryptionPublic, signingPublic),
    encryption: {
      publicKey: toBase64Url(encryptionPublic),
      privateKey: privateJwk(await subtle.exportKey('jwk', encryption.privateKey)),
    },
    signing: {
      publicKey: toBase64Url(signingPublic),
      privateKey: privateJwk(await subtle.exportKey('jwk', signing.privateKey)),
    },
  };
  return { keys: await loadDeviceKeys(material), material };
}

/** The body for POST /api/devices, including the signed proof that this device holds its signing key. */
export async function deviceRegistration(keys: DeviceKeys, accountId: string, label: string) {
  const proof = await signBytes(keys.signingPrivate, deviceRegistrationBytes(accountId, keys.id));
  return {
    label,
    encryptionKey: toBase64Url(keys.encryptionPublic),
    signingKey: toBase64Url(keys.signingPublic),
    proof: toBase64Url(proof),
  };
}
